import { useMemo } from "react";
import apiClient from "./apiClient";
import { Camera } from "../state/types";
import { stripUnitSuffix } from "../utils/systemCode";

const parseCameraPayload = (payload: unknown): Camera[] => {
  if (Array.isArray(payload)) {
    return payload as Camera[];
  }

  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return Array.isArray(parsed) ? (parsed as Camera[]) : [];
    } catch (error) {
      console.error("Failed to parse camera payload:", error);
      return [];
    }
  }

  return [];
};

const serializeCamera = (camera: Camera, systemCode: string) => {
  const normalizedSystemCode = stripUnitSuffix(systemCode);
  const cameraId = camera.cameraId ?? camera.id;
  const cameraType = camera.type ?? camera.camera_type;
  const ipAddress = camera.ip_address ?? camera.http_config?.ip_address ?? "";
  const queryParams =
    camera.query_params !== undefined ? camera.query_params : camera.http_config?.query_params ?? null;
  const username = camera.username ?? camera.http_config?.username ?? "";
  const password = camera.password ?? camera.http_config?.password ?? "";
  const order = Number(camera.order ?? camera.sort_position ?? camera.camera_position ?? 0) || 0;

  return {
    ...camera,
    systemCode: normalizedSystemCode,
    cameraId,
    id: cameraId,
    type: cameraType,
    camera_type: cameraType,
    order,
    sort_position: order,
    ip_address: ipAddress,
    query_params: queryParams,
    username,
    password,
    capture_protocol: camera.brand?.toLowerCase() === "axis" ? "http" : "mqtt",
    http_config: {
      ...(camera.http_config || {}),
      ip_address: ipAddress,
      username,
      password,
      query_params: queryParams,
      has_credentials: Boolean(username || password),
    },
    desired_settings: {
      ...(camera.desired_settings || {}),
      skip_ricoh_while_charging: Boolean(camera.skip_ricoh_while_charging),
    },
  };
};

export const useCameraApi = () => {
  return useMemo(() => {
    return {
      listCamerasApi: async (systemCode: string): Promise<Camera[]> => {
        const { data } = await apiClient.post("/camera", {
          system_code: stripUnitSuffix(systemCode),
        });

        return parseCameraPayload(data);
      },

      createCameraApi: async (camera: Camera, systemCode: string) => {
        const payload = serializeCamera(camera, systemCode);

        await apiClient.post("/camera/create", payload);
        return payload as Camera;
      },

      updateCameraApi: async (camera: Camera, systemCode: string) => {
        const payload = serializeCamera(camera, systemCode);

        await apiClient.put("/camera", payload);
        return payload as Camera;
      },

      batchUpdateCamerasApi: async (cameras: Camera[], systemCode: string) => {
        const payload = cameras.map((camera) => serializeCamera(camera, systemCode));

        await apiClient.put("/camera/batch", {
          systemCode: stripUnitSuffix(systemCode),
          cameras: payload,
        });

        return payload as Camera[];
      },

      deleteCameraApi: async (cameraId: string | number, systemCode: string) => {
        await apiClient.delete("/camera", {
          data: {
            cameraId,
            systemCode: stripUnitSuffix(systemCode),
          },
        });
      },
    };
  }, []);
};
